import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AppService } from "@App/app.service";
import { AccountSettingsDTO } from "@DTOs";
import { AccountSettings } from "@Enums";
import { Account, UWResponse } from "@Models";
import { AccountSettingsDataService } from "@Services";
import { SharedModule } from "@Shared/shared.module";
import { Utils } from "@Utils";
import { catchError, forkJoin, map, of, take } from "rxjs";
import { SubSink } from "subsink";

interface IntegrationOption {
    integrationId: string;
    name: string;
}

interface ExcludedIntegrationEntry {
    integrationId: string;
}

@Component({
    selector: 'workbenchv2-suppress-integrations-config',
    templateUrl: './workbenchv2-suppress-integrations-config.component.html',
    imports: [SharedModule]
})
export default class WorkbenchV2SuppressIntegrationsConfigComponent implements OnInit, OnDestroy {
    @Input() account: Account;

    integrations: IntegrationOption[] = [];
    selectedIntegrationIds: string[] = [];
    dataLoaded = false;
    errorMessage = '';

    private accountSettingId: string;

    subs = new SubSink();

    constructor(
        private readonly appService: AppService,
        private readonly accountSettingsDataService: AccountSettingsDataService,
    ) { }

    ngOnInit() {
        this.loadData();
    }

    private loadData() {
            forkJoin({
                integrations: this.appService.getData(`Accounts/${this.account.id}/Integrations`),
                accountSettings: this.accountSettingsDataService.loadAccountSettings(this.account.id).pipe(take(1))
            }).subscribe({
                next: ({ integrations, accountSettings }) => {
                    const existingSetting = (accountSettings as AccountSettingsDTO[])
                        .find(x => x.name === AccountSettings.uwwCaseRequirementExcludedIntegrations);

                    this.accountSettingId = existingSetting?.id;

                    const excludedIds = this.parseExcludedIntegrations(existingSetting?.value);

                    const options: IntegrationOption[] = (integrations as any)?.data?.map(integration => ({
                        integrationId: integration.id,
                        name: integration.name,
                    })) ?? [];

                    this.integrations = options.sort((a, b) => a.name.localeCompare(b.name));
                    this.selectedIntegrationIds = [...excludedIds];
                    this.dataLoaded = true;
                }
            })
        
    }

    private parseExcludedIntegrations(value: string): Set<string> {
        if (!value) return new Set();
        try {
            const parsed: ExcludedIntegrationEntry[] = JSON.parse(value);
            return new Set(parsed.map(x => x.integrationId));
        } catch {
            return new Set();
        }
    }

    onSelectionChange(selectedIds: string[]) {
        this.selectedIntegrationIds = selectedIds;
        this.save();
    }

    private save() {
        const excluded: ExcludedIntegrationEntry[] = this.selectedIntegrationIds
            .map(id => ({ integrationId: id }));

        const updateAccountSetting: AccountSettingsDTO = {
            id: this.accountSettingId ?? Utils.emptyGuid,
            accountId: this.account.id,
            name: AccountSettings.uwwCaseRequirementExcludedIntegrations,
            value: JSON.stringify(excluded)
        };

        this.appService.postData<UWResponse<AccountSettingsDTO[]>>('AccountSettings', [updateAccountSetting]).pipe(
            map((response) => {
                const saved = response?.data?.[0];
                if (saved) {
                    this.accountSettingId = saved.id;
                }
                this.accountSettingsDataService.reloadAccountSettings(this.account.id).subscribe();
                return true;
            }),
            catchError((err: Error) => {
                this.errorMessage = err.message;
                return of(false);
            }),
        ).subscribe();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
}
